import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Grid,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import LoadingSpinner from './LoadingSpinner';
import { useTheme, useMediaQuery } from '@mui/material';  // Import useTheme and useMediaQuery
import ColorPicker from './ColorPicker';
import CustomTimeDatePicker from './CustomTimeDatePicker';

const mobileEventOptionLabelSx = {
  m: 0,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  alignItems: 'center',
  gap: 0.5,
  '& .MuiTypography-root': {
    fontSize: '0.75rem',
    lineHeight: 1.15,
  },
};

const mobileOptionGridItemSx = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 32,
  minWidth: 0,
};

function CustomEventPopup({
  isOpen,
  onClose,
  headerText,
  anchor,
  items,
  onDelete,
  onAvailabilityChange,
  onHandleEventXChange,
  onHandleEventInvoicedChange,
  popupEventTitle,
  titleChange,
  popupEventDescription,
  descriptionChange,
  selectedColor,
  changeColor,
  openColorPicker,
  availability,
  showAvailabilitySelect,
  isX,
  isEditable,
  popupEventAllDay,
  allDayChange,
  popupEventDate,
  changeStartDate,
  changeEndDate,
  startRef,
  endRef,
  isChecked,
  handleResourceCheckInEvent, // Pass the function to the component
  popupButtons,
  responsivePopup,
  closeOnOverlayClick,
  saveEvent,
  setOpen,
  isEdit,
  isManualSaving,
}) {
  const [invoiced, setInvoiced] = useState(isEditable || false);
  const [filterText, setFilterText] = useState('');
  const [crewItems, setCrewItems] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const textFieldRef = useRef(null);

  const theme = useTheme();  // Initialize useTheme hook
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')); // 'sm' is the breakpoint for small screens
  const isSaveDisabled = Boolean(isManualSaving);

  useEffect(() => {
    if (isOpen) {
      console.log("useEffect handleDialogEntered")
      const timer = setTimeout(() => {
        // Only focus the input after dialog is fully rendered
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 300); // Add a small delay (200ms)

      return () => clearTimeout(timer); // Cleanup timeout on component unmount or isOpen change
    }
  }, [isOpen]); // Trigger effect whenever isOpen changes


  useEffect(() => {
    setInvoiced(!!isEditable);
  }, [isEditable]);

  useEffect(() => {
    if (items && Array.isArray(items)) {
      // Filter based on parent structure instead of type property
      // Crew items are those with parentId pointing to "Crew" (id: 100) or are direct children of Crew
      // Equipment items are those with parentId pointing to "Equipment" (id: 101) or are direct children of Equipment
      setCrewItems(items.filter((item) => {
        // Check if item has type property (old system) or is under Crew parent (new system)
        return item.type === 'person' || 
               (item.parentId === 100) || 
               (item.parent && item.parent.id === 100) ||
               (item.parent && item.parent.name === 'Crew');
      }));
      
      setEquipmentItems(items.filter((item) => {
        // Check if item has type property (old system) or is under Equipment parent (new system)
        return item.type === 'equipment' || 
               (item.parentId === 101) || 
               (item.parent && item.parent.id === 101) ||
               (item.parent && item.parent.name === 'Equipment');
      }));
      
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [items, isOpen]);

  const handleFilterChange = (event) => setFilterText(event.target.value);

  const filteredCrewItems = crewItems.filter((item) =>
    item.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  const filteredEquipmentItems = equipmentItems.filter((item) =>
    item.name?.toLowerCase().includes(filterText.toLowerCase())
  );


  // Function to handle keyDown event

  // Add event listener on mount and remove it on unmount
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen || isSaveDisabled || event.key !== 'Enter' || event.repeat || event.defaultPrevented) {
        return;
      }

      if (event.target?.tagName === 'TEXTAREA' || event.isComposing) {
        return;
      }

      event.preventDefault();
      saveEvent();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isSaveDisabled, saveEvent]);  // Dependencies to re-add the listener when needed



  if (loading) {
    return (
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{headerText}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner size={40} fullHeight={false} />
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog
      disableRestoreFocus
      open={isOpen}
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      anchor={anchor}
      disableEscapeKeyDown={!closeOnOverlayClick}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',  // Full width by default
          '@media (max-width: 600px)': {
            width: '100%', // Full width on small screens
          },
          '@media (min-width: 600px) and (max-width: 900px)': {
            width: '80%', // 80% width on medium screens
          },
          '@media (min-width: 900px)': {
            width: '70%', // 70% width on larger screens
          },
        },
        '& .MuiDialogTitle-root': {
          paddingBottom: '10px', // Remove any bottom padding from the title
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", // Soft shadow under the title
          zIndex: 2, // Ensures it stays above content
        },
        '& .MuiDialogContent-root': {
          paddingTop: '35px !important', // Add space below the title
        },
        '& .MuiDialogActions-root': {
          justifyContent: 'space-around',
          boxShadow: "0px -2px 1px rgba(0, 0, 0, 0.05)", // Soft shadow above the buttons
          zIndex: 2, // Ensures it's above content
        }
      }}
    >
      <DialogTitle>{headerText}</DialogTitle>

      <DialogContent sx={{ pt: 2, }}>
        <Stack spacing={2}>
          <TextField
            inputRef={textFieldRef} // Apply ref for focus
            // autoFocus
            label="Title"
            value={popupEventTitle}
            onChange={(e) => {
              titleChange(e);
            }}
            variant="outlined"
            fullWidth
          />

          <TextField
            label="Description"
            value={popupEventDescription}
            onChange={(e) => {
              descriptionChange(e);
            }}
            variant="outlined"
            multiline
            fullWidth
          />


          <Box sx={{ width: '100%' }}>
            {fullScreen ? (
              <Grid
                container
                columnSpacing={1.5}
                rowSpacing={0.75}
                alignItems="center"
                sx={{ py: 0.25 }}
              >
                <Grid item xs={4} sx={mobileOptionGridItemSx}>
                  <FormControlLabel
                    sx={mobileEventOptionLabelSx}
                    control={
                      <ColorPicker
                        compact
                        controlOnly
                        selectedColor={selectedColor}
                        changeColor={changeColor}
                      />
                    }
                    label="Color"
                  />
                </Grid>
                <Grid item xs={4} sx={mobileOptionGridItemSx}>
                  <FormControlLabel
                    sx={mobileEventOptionLabelSx}
                    control={
                      <Checkbox
                        size="small"
                        checked={popupEventAllDay}
                        onChange={(e) => allDayChange(e)}
                      />
                    }
                    label="All Day"
                  />
                </Grid>
                <Grid item xs={4} sx={mobileOptionGridItemSx}>
                  <FormControlLabel
                    sx={mobileEventOptionLabelSx}
                    control={
                      <Checkbox
                        size="small"
                        checked={invoiced}
                        onChange={(e) => {
                          setInvoiced(e.target.checked);
                          onHandleEventInvoicedChange(e);
                        }}
                      />
                    }
                    label="Closed"
                  />
                </Grid>
                {showAvailabilitySelect ? (
                  <Grid item xs={12} sx={{ ...mobileOptionGridItemSx, alignItems: 'stretch' }}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel id="event-availability-label">Tilgjengelighet</InputLabel>
                      <Select
                        labelId="event-availability-label"
                        label="Tilgjengelighet"
                        value={availability ?? ''}
                        onChange={onAvailabilityChange}
                      >
                        <MenuItem value="">
                          <em>Ingen</em>
                        </MenuItem>
                        <MenuItem value="busy">Opptatt</MenuItem>
                        <MenuItem value="vacation">Ferie</MenuItem>
                        <MenuItem value="sick">Syk</MenuItem>
                        <MenuItem value="sickChild">Syke barn</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}
              </Grid>
            ) : (
              <Stack spacing={1.25} sx={{ width: '100%' }}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  columnGap={2}
                  rowGap={1}
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '15px',
                      display: 'inline',
                      fontWeight: '300',
                    },
                  }}
                >
                  <ColorPicker selectedColor={selectedColor} changeColor={changeColor} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={popupEventAllDay}
                        onChange={(e) => allDayChange(e)}
                      />
                    }
                    label="All Day"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={invoiced}
                        onChange={(e) => {
                          setInvoiced(e.target.checked);
                          onHandleEventInvoicedChange(e);
                        }}
                      />
                    }
                    label="Closed"
                  />
                </Stack>
                {showAvailabilitySelect ? (
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 220, maxWidth: '100%' }}>
                    <InputLabel id="event-availability-label-desktop">Tilgjengelighet</InputLabel>
                    <Select
                      labelId="event-availability-label-desktop"
                      label="Tilgjengelighet"
                      value={availability ?? ''}
                      onChange={onAvailabilityChange}
                    >
                      <MenuItem value="">
                        <em>Ingen</em>
                      </MenuItem>
                      <MenuItem value="busy">Opptatt</MenuItem>
                      <MenuItem value="vacation">Ferie</MenuItem>
                      <MenuItem value="sick">Syk</MenuItem>
                      <MenuItem value="sickChild">Syke barn</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
              </Stack>
            )}
          </Box>

          <CustomTimeDatePicker
            popupEventDate={popupEventDate}
            changeStartDate={changeStartDate}
            changeEndDate={changeEndDate}
            popupEventAllDay={popupEventAllDay}
          />

          <TextField
            label="Filter Items"
            value={filterText}
            onChange={handleFilterChange}
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
          />

          <Typography variant="h6">Crew</Typography>
          <List>
            <Grid container spacing={0}>
              {filteredCrewItems.map((item) => {
                return (item.eventSelectable !== false) ? (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <ListItem disablePadding>
                      <ListItemButton dense onClick={() => handleResourceCheckInEvent(item)}>
                        <ListItemIcon>
                          <Checkbox checked={isChecked.includes(item.id)} />
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                ) : null;
              })}
            </Grid>
          </List>

          <Typography variant="h6">Equipment</Typography>
          <List>
            <Grid container spacing={0}>
              {filteredEquipmentItems.map((item) => {
                return (item.eventSelectable !== false) ? (
                  <Grid item xs={12} sm={6} key={item.id}>
                    <ListItem disablePadding>
                      <ListItemButton dense onClick={() => handleResourceCheckInEvent(item)}>
                        <ListItemIcon>
                          <Checkbox checked={isChecked.includes(item.id)} />
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                      </ListItemButton>
                    </ListItem>
                  </Grid>
                ) : null;
              })}
            </Grid>
          </List>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-around',
          boxShadow: "rgba(0, 0, 0, 0.2) 0px 3px 5px -1px, rgba(0, 0, 0, 0.14) 0px 6px 10px 0px, rgba(0, 0, 0, 0.12) 0px 1px 18px 0px"
        }}>
        {isEdit ? <>

          <Button
            sx={{
              border: '1px solid rgba(255, 6, 6, 0.62)', // Blue border
              backgroundColor: 'white', // White background
              color: '#3498B7', // Blue text color
              '&:hover': {
                backgroundColor: 'rgba(255, 6, 6, 0.62)', // Blue background on hover
                color: 'white', // White text on hover
                borderColor: 'rgba(255, 6, 6, 0.62)', // Darker blue border on hover
              },
            }}
            onClick={() => onDelete()} // Your cancel handler function
            variant="outlined"
            color="secondary"
          >
            Delete
          </Button>
          <Button
            sx={{
              border: '1px solid #3498B7', // Blue border
              backgroundColor: 'white', // White background
              color: '#3498B7', // Blue text color
              '&:hover': {
                backgroundColor: '#3498B7', // Blue background on hover
                color: 'white', // White text on hover
                borderColor: '#2980B9', // Darker blue border on hover
              },
            }}
            onClick={onClose} // Your cancel handler function
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            sx={{
              border: '1px solid #3498B7', // Blue border
              backgroundColor: 'white', // White background
              color: '#3498B7', // Blue text color
              '&:hover': {
                backgroundColor: '#3498B7', // Blue background on hover
                color: 'white', // White text on hover
                borderColor: '#2980B9', // Darker blue border on hover
              },
            }}
            onClick={saveEvent}
            disabled={isSaveDisabled}
            variant="outlined"
            color="secondary"
          >
            {isSaveDisabled ? 'Saving...' : 'Save'}
          </Button>
        </> :
          <>
            <Button
              sx={{
                border: '1px solid #3498B7', // Blue border
                backgroundColor: 'white', // White background
                color: '#3498B7', // Blue text color
                '&:hover': {
                  backgroundColor: '#3498B7', // Blue background on hover
                  color: 'white', // White text on hover
                  borderColor: '#2980B9', // Darker blue border on hover
                },
              }}
              onClick={onClose} // Your cancel handler function
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              sx={{
                border: '1px solid #3498B7', // Blue border
                backgroundColor: 'white', // White background
                color: '#3498B7', // Blue text color
                '&:hover': {
                  backgroundColor: '#3498B7', // Blue background on hover
                  color: 'white', // White text on hover
                  borderColor: '#2980B9', // Darker blue border on hover
                },
              }}
              onClick={saveEvent}
              disabled={isSaveDisabled}
              variant="outlined"
              color="secondary"
            >
              {isSaveDisabled ? 'Saving...' : 'Add'}
            </Button>

          </>
        }

      </DialogActions>
    </Dialog >
  );
}

export default CustomEventPopup;